import { BufferAttribute, BufferGeometry } from 'three';
import { Brush, Evaluator, INTERSECTION } from 'three-bvh-csg';
import type { TriangleSoup } from './design-region-bounds';

/** The parts to cut the overlap from, as non-indexed triangles in one frame. */
export interface OverlapSolidsRequest {
  design: { id: string; soup: TriangleSoup }[];
  nondesign: TriangleSoup[];
}

/** The solid one design part shares with the non-design parts, as non-indexed triangles. */
export interface OverlapSolid {
  id: string;
  positions: Float32Array;
}

/** Messages from the design-overlap worker. */
export type OverlapSolidsMessage =
  | { type: 'progress'; done: number; total: number }
  | { type: 'done'; solids: OverlapSolid[] }
  | { type: 'error'; message: string };

function brushOf(soup: TriangleSoup): Brush {
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(soup, 3));
  geometry.computeBoundingBox();
  const brush = new Brush(geometry);
  brush.updateMatrixWorld();
  return brush;
}

function positionsOf(pieces: readonly BufferGeometry[]): Float32Array {
  const flat = pieces.map((geometry) => (geometry.index ? geometry.toNonIndexed() : geometry));
  let count = 0;
  for (const geometry of flat) count += geometry.getAttribute('position').array.length;
  const positions = new Float32Array(count);
  let offset = 0;
  for (const geometry of flat) {
    const piece = geometry.getAttribute('position').array;
    positions.set(piece, offset);
    offset += piece.length;
  }
  for (const geometry of new Set([...pieces, ...flat])) geometry.dispose();
  return positions;
}

/**
 * Cuts, for each design part, the solid it shares with the non-design parts:
 * one intersection per design and non-design pair whose bounds meet. A pair
 * the intersection cannot be cut from (broken meshes) is left out. Parts
 * sharing nothing are left out of the result. `onProgress` follows each pair.
 */
export function overlapSolids(
  request: OverlapSolidsRequest,
  onProgress?: (done: number, total: number) => void
): OverlapSolid[] {
  const cutters = request.nondesign.map(brushOf);
  const parts = request.design.map(({ id, soup }) => ({ id, brush: brushOf(soup) }));
  const pairs = parts.map(({ brush }) =>
    cutters.filter((cutter) => brush.geometry.boundingBox!.intersectsBox(cutter.geometry.boundingBox!))
  );
  const total = pairs.reduce((sum, meeting) => sum + meeting.length, 0);
  let done = 0;
  onProgress?.(done, total);

  const evaluator = new Evaluator();
  evaluator.attributes = ['position'];
  evaluator.useGroups = false;
  const solids: OverlapSolid[] = [];
  parts.forEach(({ id, brush }, index) => {
    const pieces: BufferGeometry[] = [];
    for (const cutter of pairs[index]) {
      try {
        const result = evaluator.evaluate(brush, cutter, INTERSECTION);
        if (result.geometry.getAttribute('position').count > 0) pieces.push(result.geometry);
        else result.geometry.dispose();
      } catch {
        // Left undrawn.
      }
      onProgress?.(++done, total);
    }
    brush.geometry.dispose();
    if (pieces.length > 0) solids.push({ id, positions: positionsOf(pieces) });
  });
  for (const cutter of cutters) cutter.geometry.dispose();
  return solids;
}
